import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { ApClassFormatStaff } from "./ApClassFormatStaff";
import type { ApClassStaff } from "./ApClassStaff";
import type { ApClassTypeDTOBase } from "./ApClassTypeDTOBase";
import type { ApClassTypeOverrideStaff } from "./ApClassTypeOverrideStaff";
import type { ClassRequirementMode } from "./ClassRequirementMode";
import type { RatingRef } from "./RatingRef";
import type { RatingStaff } from "./RatingStaff";
import type { TrackedBase } from "./TrackedBase";

export class ApClassTypeStaff implements ApClassTypeDTOBase, TrackedBase {
  constructor(public readonly typeId: number, public readonly typeName: string, public readonly ratingPrereqId: RatingStaff | null, public readonly ratingOverkillId: RatingStaff | null, public readonly classPrereqId: ApClassTypeStaff | null, public readonly classOverkillId: ApClassTypeStaff | null, public readonly displayOrder: number, public readonly descLong: string | null, public readonly descShort: string | null, public readonly colorCode: string | null, public readonly noSignup: string | null, public readonly priceDefault: number | null, public readonly signupMaxDefault: number | null, public readonly signupMinDefault: number | null, public readonly disallowIfOverkill: string | null, public readonly teachUrl: string | null, public readonly prereqMustComplete: boolean | null, public readonly allowTeacher: boolean | null, public readonly teachRatingId: RatingStaff | null, public readonly prereqMode: ClassRequirementMode, public readonly overkillMode: ClassRequirementMode, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly formats: Array<ApClassFormatStaff> | null, public readonly typesRequiringThisAsPrereq: Array<ApClassTypeStaff> | null, public readonly typesRequiringThisAsOverkill: Array<ApClassTypeStaff> | null, public readonly classes: Array<ApClassStaff> | null, public readonly overrides: Array<ApClassTypeOverrideStaff> | null, public readonly ratingPrereqIdTargetIds: RatingRef | null, public readonly ratingOverkillIdTargetIds: RatingRef | null, public readonly teachRatingIdTargetIds: RatingRef | null) {
    this.cbiServiceContext = null;
    this.ratingPrereqIdLoaded = this.ratingPrereqId != null;
    this.ratingPrereqIdCached = this.ratingPrereqId;
    this.ratingOverkillIdLoaded = this.ratingOverkillId != null;
    this.ratingOverkillIdCached = this.ratingOverkillId;
    this.classPrereqIdLoaded = this.classPrereqId != null;
    this.classPrereqIdCached = this.classPrereqId;
    this.classOverkillIdLoaded = this.classOverkillId != null;
    this.classOverkillIdCached = this.classOverkillId;
    this.teachRatingIdLoaded = this.teachRatingId != null;
    this.teachRatingIdCached = this.teachRatingId;
    this.formatsLoaded = this.formats != null;
    this.formatsCached = this.formats;
    this.typesRequiringThisAsPrereqLoaded = this.typesRequiringThisAsPrereq != null;
    this.typesRequiringThisAsPrereqCached = this.typesRequiringThisAsPrereq;
    this.typesRequiringThisAsOverkillLoaded = this.typesRequiringThisAsOverkill != null;
    this.typesRequiringThisAsOverkillCached = this.typesRequiringThisAsOverkill;
    this.classesLoaded = this.classes != null;
    this.classesCached = this.classes;
    this.overridesLoaded = this.overrides != null;
    this.overridesCached = this.overrides;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private ratingPrereqIdLoaded: boolean;
  private ratingPrereqIdCached: RatingStaff | null;
  private ratingOverkillIdLoaded: boolean;
  private ratingOverkillIdCached: RatingStaff | null;
  private classPrereqIdLoaded: boolean;
  private classPrereqIdCached: ApClassTypeStaff | null;
  private classOverkillIdLoaded: boolean;
  private classOverkillIdCached: ApClassTypeStaff | null;
  private teachRatingIdLoaded: boolean;
  private teachRatingIdCached: RatingStaff | null;
  private formatsLoaded: boolean;
  private formatsCached: Array<ApClassFormatStaff> | null;
  private typesRequiringThisAsPrereqLoaded: boolean;
  private typesRequiringThisAsPrereqCached: Array<ApClassTypeStaff> | null;
  private typesRequiringThisAsOverkillLoaded: boolean;
  private typesRequiringThisAsOverkillCached: Array<ApClassTypeStaff> | null;
  private classesLoaded: boolean;
  private classesCached: Array<ApClassStaff> | null;
  private overridesLoaded: boolean;
  private overridesCached: Array<ApClassTypeOverrideStaff> | null;
  async ratingPrereqIdLazy(): Promise<RatingStaff | null> {
    if (!this.ratingPrereqIdLoaded) {
      this.ratingPrereqIdCached = await (this.ratingPrereqIdTargetIds != null ? this.cbiServiceContext!.ratingService().getRatingStaff(this.ratingPrereqIdTargetIds.ratingId) : Promise.resolve(null));
      this.ratingPrereqIdLoaded = true;
    }
    return this.ratingPrereqIdCached;
  }
  async ratingOverkillIdLazy(): Promise<RatingStaff | null> {
    if (!this.ratingOverkillIdLoaded) {
      this.ratingOverkillIdCached = await (this.ratingOverkillIdTargetIds != null ? this.cbiServiceContext!.ratingService().getRatingStaff(this.ratingOverkillIdTargetIds.ratingId) : Promise.resolve(null));
      this.ratingOverkillIdLoaded = true;
    }
    return this.ratingOverkillIdCached;
  }
  async classPrereqIdLazy(): Promise<ApClassTypeStaff | null> {
    if (!this.classPrereqIdLoaded) {
      this.classPrereqIdCached = await this.cbiServiceContext!.apClassService().getApClassTypeStaffClassPrereqId(this.typeId);
      this.classPrereqIdLoaded = true;
    }
    return this.classPrereqIdCached;
  }
  async classOverkillIdLazy(): Promise<ApClassTypeStaff | null> {
    if (!this.classOverkillIdLoaded) {
      this.classOverkillIdCached = await this.cbiServiceContext!.apClassService().getApClassTypeStaffClassOverkillId(this.typeId);
      this.classOverkillIdLoaded = true;
    }
    return this.classOverkillIdCached;
  }
  async teachRatingIdLazy(): Promise<RatingStaff | null> {
    if (!this.teachRatingIdLoaded) {
      this.teachRatingIdCached = await (this.teachRatingIdTargetIds != null ? this.cbiServiceContext!.ratingService().getRatingStaff(this.teachRatingIdTargetIds.ratingId) : Promise.resolve(null));
      this.teachRatingIdLoaded = true;
    }
    return this.teachRatingIdCached;
  }
  async formatsLazy(): Promise<Array<ApClassFormatStaff>> {
    if (!this.formatsLoaded) {
      this.formatsCached = await this.cbiServiceContext!.apClassService().getApClassTypeStaffFormats(this.typeId);
      this.formatsLoaded = true;
    }
    return this.formatsCached!;
  }
  async typesRequiringThisAsPrereqLazy(): Promise<Array<ApClassTypeStaff>> {
    if (!this.typesRequiringThisAsPrereqLoaded) {
      this.typesRequiringThisAsPrereqCached = await this.cbiServiceContext!.apClassService().getApClassTypeStaffTypesRequiringThisAsPrereq(this.typeId);
      this.typesRequiringThisAsPrereqLoaded = true;
    }
    return this.typesRequiringThisAsPrereqCached!;
  }
  async typesRequiringThisAsOverkillLazy(): Promise<Array<ApClassTypeStaff>> {
    if (!this.typesRequiringThisAsOverkillLoaded) {
      this.typesRequiringThisAsOverkillCached = await this.cbiServiceContext!.apClassService().getApClassTypeStaffTypesRequiringThisAsOverkill(this.typeId);
      this.typesRequiringThisAsOverkillLoaded = true;
    }
    return this.typesRequiringThisAsOverkillCached!;
  }
  async classesLazy(): Promise<Array<ApClassStaff>> {
    if (!this.classesLoaded) {
      this.classesCached = await this.cbiServiceContext!.apClassService().getApClassTypeStaffClasses(this.typeId);
      this.classesLoaded = true;
    }
    return this.classesCached!;
  }
  async overridesLazy(): Promise<Array<ApClassTypeOverrideStaff>> {
    if (!this.overridesLoaded) {
      this.overridesCached = await this.cbiServiceContext!.apClassService().getApClassTypeStaffOverrides(this.typeId);
      this.overridesLoaded = true;
    }
    return this.overridesCached!;
  }
}
